// Tiny fetch wrapper. Reads VITE_API_BASE_URL at build time, attaches the
// JWT from localStorage on every call, and parses JSON envelope on success.
//
// On 401 we clear the stored token so the router guard punts the user back
// to /login on the next navigation.

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''
const TOKEN_KEY = 'ce.vin:auth-token'

/**
 * The API's origin (scheme+host+port, no path) — used by the SCORM
 * cross-origin bridge (scormBridgeHost.ts) to verify `event.origin` on
 * inbound `postMessage`s from the wrapper iframe and to set an explicit
 * (never `'*'`) `targetOrigin` on outbound ones. Derived from the SAME
 * config `apiRequest` already reads, not a separate hardcoded value.
 *
 * `BASE_URL` empty means dev's single-process setup (Vite proxies `/v1`
 * to the local API — see vite.config.ts), where the API is effectively
 * served from this page's own origin; falling back to
 * `window.location.origin` matches that.
 */
export function getApiOrigin(): string {
  if (!BASE_URL) return window.location.origin
  try {
    return new URL(BASE_URL).origin
  } catch {
    return window.location.origin
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message: string
  ) {
    super(message)
  }
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setStoredToken(token: string | null): void {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token)
  } else {
    localStorage.removeItem(TOKEN_KEY)
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  headers?: Record<string, string>
  auth?: boolean
  /**
   * Return the response body as TEXT, exactly as the server wrote it, instead
   * of parsing it and leaving the caller to re-serialise.
   *
   * Needed for VIN JSON: `vin.dumps` keeps `module_order` and `segment_order`
   * on ONE line (PROJECT-CONTEXT §2 / Rule 12), and `validate.py` reports the
   * expanded form as a BLOCKING problem. A parse-then-`JSON.stringify(…, 2)`
   * round trip re-expands both arrays, so the operator would paste text this
   * repo's own validator rejects.
   */
  raw?: boolean
}

/**
 * A binary response body, with the same auth as `apiRequest`.
 *
 * `apiRequest` parses its body as JSON (or text) and would corrupt zip bytes.
 * A plain `<a href>` cannot be used instead: these routes require the
 * `Authorization` header, which a browser navigation does not send. So the
 * bytes come back through fetch and the caller turns the Blob into a save.
 */
export async function apiBlob(path: string): Promise<Blob> {
  const headers: Record<string, string> = {}
  const token = getStoredToken()
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${BASE_URL}${path}`, { headers })
  if (res.status === 401) setStoredToken(null)
  if (!res.ok) {
    // Same shape as `apiRequest`'s failure path: the server's own detail if it
    // sent one, so "no package named ... in this conversion" reaches the
    // operator instead of a bare status code.
    let detail = `request failed (${res.status})`
    let body: unknown = null
    try {
      body = await res.json()
      const parsed = body as { detail?: unknown }
      if (parsed && typeof parsed.detail === 'string') detail = parsed.detail
    } catch {
      /* not JSON: keep the status-code message */
    }
    throw new ApiError(res.status, body, detail)
  }
  return res.blob()
}


/**
 * A server error `detail` as text an operator can act on.
 *
 * THE BUG THIS CLOSES, reported 2026-08-20 from the media panel: the studio
 * showed "Could not save / [object Object],[object Object]". `detail` was typed
 * here as `string`, but FastAPI returns a LIST for every validation error, and
 * handing that list to `ApiError.message` meant any template rendering it
 * stringified the objects. The server had said exactly what was wrong -- a VIN
 * document id typed into the duration box, which is capped at 86,400 seconds --
 * once per row, and the operator's only surviving clue was the row count. This
 * destroyed the message of EVERY 422 in the app, not just this one.
 *
 * NUMERIC PATH SEGMENTS ARE DROPPED from `loc`, which does two jobs: the field
 * name is what diagnoses the mistake (the VALUE was fine for the box the
 * operator thought they were filling in), and dropping the row index makes the
 * same mistake on twelve rows dedupe to one sentence instead of twelve.
 */
function formatDetail(detail: unknown, fallback: string): string {
  if (typeof detail === 'string' && detail.trim() !== '') return detail
  if (Array.isArray(detail)) {
    const parts: string[] = []
    for (const entry of detail) {
      if (entry === null || typeof entry !== 'object') {
        const text = String(entry ?? '').trim()
        if (text && !parts.includes(text)) parts.push(text)
        continue
      }
      const e = entry as { loc?: unknown; msg?: unknown }
      const field = Array.isArray(e.loc)
        ? e.loc.filter((p) => typeof p === 'string' && p !== 'body').join('.')
        : ''
      const msg = typeof e.msg === 'string' && e.msg ? e.msg : JSON.stringify(entry)
      const text = field ? `${field}: ${msg}` : msg
      if (!parts.includes(text)) parts.push(text)
    }
    if (parts.length) return parts.join('; ')
  }
  return fallback
}

export async function apiRequest<T>(
  path: string,
  opts: RequestOptions = {}
): Promise<T> {
  const { method = 'GET', body, headers = {}, auth = true, raw = false } = opts

  // A `FormData` body (Task 8: the SCORM package upload — the ingest
  // endpoint takes a multipart `UploadFile`, not JSON) must reach `fetch`
  // untouched: no JSON.stringify (which would silently reduce it to the
  // string "{}") and no hand-set Content-Type — the browser fills in
  // `multipart/form-data; boundary=...` itself, and a caller-set value
  // here would ship without that boundary and break the upload.
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData

  const finalHeaders: Record<string, string> = { ...headers }
  if (body !== undefined && !isFormData) {
    finalHeaders['Content-Type'] = finalHeaders['Content-Type'] ?? 'application/json'
  }
  if (auth) {
    const token = getStoredToken()
    if (token) finalHeaders['Authorization'] = `Bearer ${token}`
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: finalHeaders,
    body: body === undefined ? undefined : isFormData ? (body as FormData) : JSON.stringify(body)
  })

  if (res.status === 401) {
    setStoredToken(null)
  }

  const text = await res.text()
  const contentType = res.headers.get('content-type') ?? ''
  const looksJson = contentType.includes('application/json') || text.startsWith('{') || text.startsWith('[')
  const parsed = text && looksJson ? safeJson(text) : null

  if (!res.ok) {
    const raw = (parsed as { detail?: unknown } | null)?.detail
    throw new ApiError(res.status, parsed, formatDetail(raw, res.statusText))
  }

  if (raw) {
    // The server's exact bytes. Placed AFTER the !res.ok branch above, so an
    // error body still surfaces as an ApiError instead of being returned as
    // text the caller would treat as a successful document.
    return text as unknown as T
  }

  if (text && !looksJson) {
    // We got a 2xx but the body isn't JSON. Almost always means BASE_URL is
    // empty and the request hit the SPA fallback HTML. Surface it loud
    // instead of pretending the call worked.
    throw new ApiError(
      res.status,
      text.slice(0, 200),
      'API responded with non-JSON content — VITE_API_BASE_URL likely missing'
    )
  }

  return parsed as T
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}
